import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones, Music, Star, Library, Trophy, Mic } from 'lucide-react';

export const AuditionAgreement: React.FC = () => {
  const navigate = useNavigate();
  
  const handleContinue = () => {
    navigate('/audition-songs');
  };
  
  return (
    <div className="min-h-screen bg-kod-purple-900 flex flex-col items-center px-4 py-8 sm:py-12">
      <div className="max-w-lg w-full flex flex-col items-center">

        {/* Logo & Brand */}
        <img
          src="/TSL Shield Purple Blue Orange White.PNG"
          alt="The Singing League"
          className="w-16 h-16 sm:w-20 sm:h-20 object-contain mb-3"
        />
        <p className="text-white/70 text-sm font-medium tracking-wide uppercase mb-8">
          The Singing League
        </p>

        {/* Hero Headline */}
        <h1 className="text-3xl sm:text-4xl font-black text-white text-center leading-tight mb-3">
          Get Your Sing Score<br />&amp; Vocal Blueprint
        </h1>
        <p className="text-white/60 text-center text-base mb-10">
          Start your journey to becoming your best vocalist.
        </p>

        {/* Step-by-step instructions */}
        <div className="w-full space-y-5 mb-12">
          <StepItem
            icon={<Music size={20} className="text-kod-accent-light" />}
            title="Choose one of the free songs available."
            description="Pick a song you love and show us what you've got!"
          />
          <StepItem
            icon={<Headphones size={20} className="text-kod-accent-light" />}
            title="Headphones or Earbuds are required for scoring."
            description="This helps us hear you clearly and score you accurately."
          />
          <StepItem
            icon={<Star size={20} className="text-kod-accent-light" />}
            title="Get your Sing Score & Vocal Blueprint."
            description="Receive a detailed breakdown of your performance and personalized tips to help you improve."
          />
        </div>

        {/* How to Use the App */}
        <div className="w-full bg-kod-purple-800 rounded-xl border border-kod-purple-600/30 p-6 mb-10">
          <h2 className="text-xl font-bold text-white text-center mb-2">How to Use the App</h2>
          <p className="text-white/50 text-sm text-center mb-6">
            Record. Get scored. Improve. Compete.<br />Everything you need is right here.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <FeatureCard
              icon={<Mic size={22} className="text-kod-accent" />}
              label="Past Recordings, Scoring & Vocal Blueprints"
            />
            <FeatureCard
              icon={<Library size={22} className="text-kod-accent" />}
              label="Song Library"
              sublabel="Browse and choose songs to sing"
            />
            <FeatureCard
              icon={<Trophy size={22} className="text-kod-accent" />}
              label="Leaderboard"
              sublabel="See how you rank and compete with others"
            />
            <FeatureCard
              icon={<Star size={22} className="text-kod-accent" />}
              label="Active and Upcoming Contests"
              sublabel="Find a contest and sign up"
            />
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleContinue}
          className="w-full max-w-sm bg-kod-accent hover:bg-kod-accent-dark text-white font-bold py-3.5 px-6 rounded-lg flex items-center justify-center transition-colors text-lg"
        >
          Pick My Song &rarr;
        </button>
        
        <p className="text-center mt-6 text-white/40 text-xs">
          Your performance will be analyzed for pitch and rhythm accuracy.
        </p>
      </div>
    </div>
  );
};

function StepItem({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-kod-purple-700 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div>
        <p className="text-white font-semibold text-sm leading-snug">{title}</p>
        <p className="text-white/60 text-sm mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function FeatureCard({ icon, label, sublabel }: { icon: React.ReactNode; label: string; sublabel?: string }) {
  return (
    <div className="bg-kod-purple-700/50 rounded-lg p-4 flex flex-col items-center text-center gap-2">
      <div className="w-10 h-10 rounded-full bg-kod-purple-600/40 flex items-center justify-center">
        {icon}
      </div>
      <p className="text-white text-xs font-semibold leading-tight">{label}</p>
      {sublabel && <p className="text-white/50 text-[11px] leading-tight">{sublabel}</p>}
    </div>
  );
}
